import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import "./Configuration.css";

const PRINTER_PROTOCOLS = [
  { value: "ESC_POS", label: "Epson ESC/POS (Standard)" },
  { value: "STAR_LINE", label: "Star Line Mode" },
  { value: "TSPL", label: "TSPL (Barcode/Label)" },
  { value: "ZPL", label: "Zebra ZPL (Label)" },
  { value: "SYSTEM", label: "System Print (HTML/OS)" }
];

const POSPrinterSettings = () => {
  const { Get, Post, Patch, Delete, getAPI } = useApi();
  const { user } = useContext(AuthContext);
  const storeId = user?.store;

  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testingId, setTestingId] = useState(null);

  // Form State
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [connectionType, setConnectionType] = useState("USB");
  const [ipAddress, setIpAddress] = useState("");
  const [port, setPort] = useState("9100");
  const [paperWidth, setPaperWidth] = useState("80MM");
  const [protocol, setProtocol] = useState("ESC_POS");

  const fetchPrinters = async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const data = await Get(`${getAPI('posPrinters')}?store_id=${storeId}`);
      setPrinters(data || []);
    } catch (error) {
      console.error("Error fetching printers:", error);
      toast.error("Failed to load printers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrinters();
  }, [storeId]);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setConnectionType("USB");
    setIpAddress("");
    setPort("9100");
    setPaperWidth("80MM");
    setProtocol("ESC_POS");
    setShowForm(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Printer name is required.");
      return;
    }

    const payload = {
      store_id: storeId,
      name,
      connection_type: connectionType,
      ip_address: connectionType === "NETWORK" ? ipAddress : null,
      port: connectionType === "NETWORK" ? Number(port) : null,
      paper_width: paperWidth,
      protocol: protocol,
    };

    try {
      if (editingId) {
        await Patch("posPrinters", editingId, payload);
        toast.success("Printer updated successfully!");
      } else {
        await Post("posPrinters", payload);
        toast.success("Printer registered successfully!");
      }
      resetForm();
      fetchPrinters();
    } catch (error) {
      console.error("Error saving printer:", error);
      toast.error(error.response?.data?.detail || "Failed to save printer.");
    }
  };

  const handleEdit = (p) => {
    setEditingId(p.id);
    setName(p.name);
    setConnectionType(p.connection_type);
    setIpAddress(p.ip_address || "");
    setPort(String(p.port || "9100"));
    setPaperWidth(p.paper_width || "80MM");
    setProtocol(p.protocol || "ESC_POS");
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to unregister this printer?")) return;
    try {
      await Delete("posPrinters", id);
      toast.success("Printer unregistered.");
      fetchPrinters();
    } catch (error) {
      console.error("Error deleting printer:", error);
      toast.error("Failed to delete printer.");
    }
  };

  const handleTestPrint = async (p) => {
    setTestingId(p.id);
    toast.info(`Sending test print receipt directly to ${p.name}...`);
    try {
      if (p.connection_type === "NETWORK") {
        // Direct RAW TCP socket print simulation
        const testUrl = `http://${p.ip_address}:${p.port || 9100}/test-print`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 4000);
        await fetch(testUrl, { method: "POST", mode: "no-cors", signal: controller.signal });
        clearTimeout(timeoutId);
        toast.success("Test print: Sent! (Network print stream dispatched)");
      } else {
        // Direct browser print simulation
        window.print();
        toast.success("Test print: Dispatched to system print dialog.");
      }
    } catch (err) {
      console.warn("Direct network print failed, checking device status:", err);
      toast.error("Test print: Offline (Printer unreachable or block in browser sandbox)");
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="responsive-box store-config-panel" style={{ marginTop: "20px" }}>
      <div className="store-config-header">
        <div>
          <span className="store-config-kicker">Device Integration</span>
          <h2>Receipt Printers</h2>
          <p>Configure local USB, Bluetooth, and TCP/IP network receipt thermal printers.</p>
        </div>
        {!showForm && (
          <button
            type="button"
            className="store-config-save"
            style={{ minWidth: "150px" }}
            onClick={() => setShowForm(true)}
          >
            + Add Printer
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSave} style={{ marginBottom: "30px" }}>
          <div className="store-config-header">
            <h3>{editingId ? "Edit Printer" : "Register Printer"}</h3>
          </div>

          <div className="store-config-grid">
            <div className="store-config-field">
              <div>
                <label htmlFor="print-name">Printer Name *</label>
                <span>Descriptive label (e.g. Receipt Printer 1).</span>
              </div>
              <input
                id="print-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Front Desk Printer"
                required
              />
            </div>

            <div className="store-config-field">
              <div>
                <label htmlFor="print-connection">Connection Type</label>
                <span>Local USB port or TCP/IP network IP.</span>
              </div>
              <select
                id="print-connection"
                value={connectionType}
                onChange={(e) => setConnectionType(e.target.value)}
              >
                <option value="USB">Direct USB / Local</option>
                <option value="NETWORK">Network IP (TCP/IP)</option>
                <option value="BLUETOOTH">Bluetooth</option>
              </select>
            </div>

            {connectionType === "NETWORK" && (
              <>
                <div className="store-config-field">
                  <div>
                    <label htmlFor="print-ip">Printer IP Address *</label>
                    <span>IP address of printer on local network.</span>
                  </div>
                  <input
                    id="print-ip"
                    type="text"
                    value={ipAddress}
                    onChange={(e) => setIpAddress(e.target.value)}
                    placeholder="e.g. 192.168.1.100"
                    required
                  />
                </div>

                <div className="store-config-field">
                  <div>
                    <label htmlFor="print-port">RAW Port</label>
                    <span>Standard raw TCP printing port (usually 9100).</span>
                  </div>
                  <input
                    id="print-port"
                    type="number"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    placeholder="9100"
                  />
                </div>
              </>
            )}

            <div className="store-config-field">
              <div>
                <label htmlFor="print-width">Paper Width</label>
                <span>Standard receipt sizes.</span>
              </div>
              <select
                id="print-width"
                value={paperWidth}
                onChange={(e) => setPaperWidth(e.target.value)}
              >
                <option value="80MM">80mm (Standard Desktop)</option>
                <option value="58MM">58mm (Mobile / Handheld)</option>
              </select>
            </div>

            <div className="store-config-field">
              <div>
                <label htmlFor="print-protocol">Printer Command Language</label>
                <span>Protocol supported by your printer.</span>
              </div>
              <select
                id="print-protocol"
                value={protocol}
                onChange={(e) => setProtocol(e.target.value)}
              >
                {PRINTER_PROTOCOLS.map((pr) => (
                  <option key={pr.value} value={pr.value}>
                    {pr.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="store-config-actions" style={{ gap: "12px", justifyContent: "flex-start", marginTop: "24px" }}>
            <button type="submit" className="store-config-save">
              Save Printer
            </button>
            <button
              type="button"
              className="store-config-reset"
              onClick={resetForm}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ color: "#64748b", fontSize: "15px" }}>Loading printers...</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "20px", marginTop: "20px" }}>
          {printers.map((p) => (
            <div
              key={p.id}
              className="store-config-field"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "14px",
                padding: "20px",
                height: "100%",
                justifyContent: "space-between"
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "850", color: "#0f172a" }}>📄 {p.name}</h4>
                  <span
                    style={{
                      fontSize: "12px",
                      background: p.enabled ? "#eefbf2" : "#fdf2f2",
                      color: p.enabled ? "#1f8a4c" : "#9b1c1c",
                      padding: "4px 10px",
                      borderRadius: "999px",
                      fontWeight: "800",
                    }}
                  >
                    {p.enabled ? "Active" : "Disabled"}
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>
                    <strong>Protocol:</strong> {PRINTER_PROTOCOLS.find(pr => pr.value === p.protocol)?.label || p.protocol}
                  </span>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>
                    <strong>Width:</strong> {p.paper_width}
                  </span>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>
                    <strong>Connection:</strong> {p.connection_type === "NETWORK" ? `IP (${p.ip_address}:${p.port || 9100})` : p.connection_type}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                <button
                  onClick={() => handleTestPrint(p)}
                  disabled={testingId === p.id}
                  className="store-config-save"
                  style={{
                    flex: 1,
                    minWidth: "auto",
                    padding: "8px 12px",
                    fontSize: "13px",
                    background: testingId === p.id ? "#94a3b8" : "#2563eb",
                    boxShadow: "none"
                  }}
                >
                  {testingId === p.id ? "Printing..." : "Test Page"}
                </button>
                <button
                  onClick={() => handleEdit(p)}
                  className="store-config-reset"
                  style={{
                    minWidth: "auto",
                    padding: "8px 16px",
                    fontSize: "13px",
                    boxShadow: "none"
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="store-config-reset"
                  style={{
                    minWidth: "auto",
                    padding: "8px 16px",
                    fontSize: "13px",
                    background: "#fef2f2",
                    color: "#b91c1c",
                    boxShadow: "none"
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}

          {printers.length === 0 && !showForm && (
            <div
              style={{
                gridColumn: "1/-1",
                textAlign: "center",
                color: "#64748b",
                padding: "40px 0",
                fontSize: "15px"
              }}
            >
              No receipt printers configured. Click "+ Add Printer" to link your hardware.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default POSPrinterSettings;
